import { extractAllBlocksFromTpl } from "@okf/ootils";
import { getTplLabel } from "./getTplLabel";

/**
 * Builds a dataset-connection graph straight from a tenant's templates.
 *
 * - nodes  = datasets / tag-collections (one per relevant template)
 * - edges  = each `tags.X` reference (the rollup links), pointing
 *            child → parent (e.g. followupInterview → baselineSurvey → …)
 * - fields = every data field on a dataset, classified by type, so the UI
 *            can show "which field is what".
 *
 * Fully dynamic: whatever templates the current tenant has is what you get.
 */

const SYSTEM_DENYLIST = new Set([
  "ooloiAdmins",
  "internalServiceUsers",
  "staticPages",
]);

// Universal meta inputs present on every template — not filter-relevant, and
// counting them would wrongly mark pure tag collections (Country, Gender…) as
// content datasets. Excluded from the field list.
const META_VALUEPATHS = new Set([
  "main.title",
  "main.subtitle",
  "main.coverImage",
  "main.images",
]);

/** Classify a single template block into a field type. */
const classifyField = (block) => {
  const valuePath = block?.valuePath || "";
  const comp = block?.comp || "";

  if (valuePath.startsWith("tags.") && block?.props?.tagType) {
    return { type: "tag", targetId: block.props.tagType };
  }
  if (Array.isArray(block?.props?.options) && block.props.options.length > 0) {
    return { type: "choice" };
  }
  if (/Number/i.test(comp)) return { type: "number" };
  if (/Date/i.test(comp)) return { type: "date" };
  if (/(Image|Video|Audio|File|Upload)/i.test(comp)) return { type: "media" };
  return { type: "text" };
};

const getFields = (tpl) => {
  let blocks = [];
  try {
    blocks = extractAllBlocksFromTpl({ tpl }) || [];
  } catch (e) {
    blocks = [];
  }
  const seen = new Set();
  const fields = [];
  for (const block of blocks) {
    const valuePath = block?.valuePath || "";
    // Only real data fields: tag references and `main.*` inputs.
    if (!valuePath.startsWith("tags.") && !valuePath.startsWith("main.")) continue;
    if (META_VALUEPATHS.has(valuePath)) continue;
    if (seen.has(valuePath)) continue;
    seen.add(valuePath);
    const { type, targetId } = classifyField(block);
    const comp = block?.comp || "";
    const rawOptions = Array.isArray(block?.props?.options)
      ? block.props.options
      : [];
    const options = rawOptions
      .map((o) => (typeof o === "string" ? o : o?.display ?? o?.value))
      .filter(Boolean);
    fields.push({
      key: valuePath,
      label: block?.props?.label || block?.props?.shortLabel || valuePath,
      type,
      targetId,
      valuePath,
      comp,
      multiple: /Multi|Checkbox/i.test(comp),
      options,
    });
  }
  return fields;
};

export const buildDatasetGraph = (allTpls = []) => {
  // 1. Per-template fields
  const fieldsByCt = {};
  for (const tpl of allTpls) {
    const ct = tpl?.kp_content_type;
    if (!ct || SYSTEM_DENYLIST.has(ct)) continue;
    fieldsByCt[ct] = { tpl, fields: getFields(tpl) };
  }

  // 2. Which content types are referenced as a tag target (so leaf tag
  //    collections with empty bodies, e.g. Country, still get a node).
  const referenced = new Set();
  for (const ct of Object.keys(fieldsByCt)) {
    for (const f of fieldsByCt[ct].fields) {
      if (f.type === "tag" && f.targetId) referenced.add(f.targetId);
    }
  }

  // 3. Nodes: any template that has fields OR is referenced as a tag target.
  const nodeIds = new Set();
  for (const ct of Object.keys(fieldsByCt)) {
    const { fields } = fieldsByCt[ct];
    if (fields.length > 0 || referenced.has(ct)) nodeIds.add(ct);
  }
  // referenced tag types might not have their own template — include anyway.
  for (const ct of referenced) nodeIds.add(ct);

  const nodes = [...nodeIds].map((ct) => {
    const entry = fieldsByCt[ct];
    const fields = entry?.fields || [];
    // "tag" = a pure classification list (referenced by others, no data fields
    // of its own beyond tag links); "content" = a dataset with real fields.
    const hasNonTagField = fields.some((f) => f.type !== "tag");
    const kind = !hasNonTagField && referenced.has(ct) ? "tag" : "content";
    return {
      id: ct,
      title: entry ? getTplLabel(entry.tpl) : ct,
      kind,
      fields,
    };
  });

  // 4. Edges from tag references (child → parent).
  const edges = [];
  for (const node of nodes) {
    for (const f of node.fields) {
      if (f.type === "tag" && f.targetId && nodeIds.has(f.targetId)) {
        edges.push({
          id: `${node.id}__${f.key}__${f.targetId}`,
          fromNodeId: node.id,
          fromFieldKey: f.key,
          toNodeId: f.targetId,
          label: f.label,
        });
      }
    }
  }

  return { nodes, edges };
};

export const FIELD_TYPE_META = {
  tag: { label: "Tag link", color: "#6938EF", bg: "#F4F0FF" },
  choice: { label: "Choice", color: "#175CD3", bg: "#EFF4FF" },
  text: { label: "Text", color: "#475467", bg: "#F2F4F7" },
  number: { label: "Number", color: "#067647", bg: "#ECFDF3" },
  date: { label: "Date", color: "#B54708", bg: "#FFFAEB" },
  media: { label: "Media", color: "#C11574", bg: "#FDF2FA" },
};
